import { useTranslation } from 'components/i18n/Translator';
import { Google } from 'components/icons/Google';
import { Microsoft } from 'components/icons/Microsoft';
import { LoaderCircle } from 'lucide-react';

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function getProviderName(provider: string) {
  switch (provider) {
    case 'azure-ad':
    case 'azure-ad-hybrid':
      return 'FON account';
    case 'github':
      return 'GitHub';
    case 'okta':
      return 'Okta';
    case 'descope':
      return 'Descope';
    case 'aws-cognito':
      return 'Cognito';
    default:
      return capitalizeFirstLetter(provider);
  }
}

function renderProviderIcon(provider: string) {
  switch (provider) {
    case 'google':
      return <Google />;
    case 'azure-ad':
    case 'azure-ad-hybrid':
      return <Microsoft />;
    default:
      return null;
  }
}

interface ProviderButtonProps {
  provider: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const ProviderButton = ({
  provider,
  onClick,
  disabled,
  loading
}: ProviderButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const isGoogle = provider.toLowerCase() === 'google';
  const isFon = provider === 'azure-ad' || provider === 'azure-ad-hybrid';

  const baseStyles =
    'fongpt-hover h-[58px] w-full rounded-xl border border-outline bg-surface/30 px-4 text-[15px] font-medium text-primary disabled:pointer-events-none disabled:opacity-60 focus-visible:outline-none flex items-center justify-center gap-3 active:scale-[0.99]';

  if (isGoogle) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={baseStyles}
      >
        {loading ? (
          <LoaderCircle aria-hidden="true" className="h-5 w-5 animate-spin" />
        ) : (
          renderProviderIcon(provider.toLowerCase())
        )}
        <span>{t('auth.provider.continueGoogle')}</span>
      </button>
    );
  }

  if (isFon) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={baseStyles}
      >
        {loading ? (
          <LoaderCircle aria-hidden="true" className="h-5 w-5 animate-spin" />
        ) : (
          renderProviderIcon(provider)
        )}
        <span>{t('auth.provider.continueFon')}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={baseStyles}
    >
      {loading ? (
        <LoaderCircle aria-hidden="true" className="h-5 w-5 animate-spin" />
      ) : (
        renderProviderIcon(provider.toLowerCase())
      )}
      <span>
        {t('auth.provider.continue', {
          provider: getProviderName(provider)
        })}
      </span>
    </button>
  );
};

export { ProviderButton };
